import { Box, Button, Typography } from '@mui/material';
import { Column } from '../../../../components/GenericTable/GenericTable';
import { CLIENT_USER_ROLE, CLIENT_USER_STATUS } from '@api/types/client-user-management';
import { formatDate } from '@utils/common';
import { ClientTableRow } from '../hooks/useClientsTable';
import {
  CLIENT_ROLE_DISPLAY_LABELS,
  CLIENT_ROLE_FILTER_OPTIONS,
  CLIENT_STATUS_DISPLAY_LABELS,
  CLIENT_STATUS_FILTER_OPTIONS,
} from '../constants';
import {
  cellTextSx,
  DashboardCell,
  renderActionsCell,
  renderCustomHeader,
  renderFilterableHeader,
  renderSortableHeader,
} from './tableColumnHelpers';
import { Add } from '@mui/icons-material';

const formatRoleLabel = (role: string) => {
  if (role === '-') return role;
  return CLIENT_ROLE_DISPLAY_LABELS[role as CLIENT_USER_ROLE] ?? role;
};

const formatStatusLabel = (status: ClientTableRow['status']) => {
  if (status === '-') return status;
  return CLIENT_STATUS_DISPLAY_LABELS[status as CLIENT_USER_STATUS] ?? status;
};

type ClientsTableColumnOptions = {
  onActionClick: (event: React.MouseEvent<HTMLElement>, row: ClientTableRow) => void;
  onEditCompany: (row: ClientTableRow) => void;
  onInviteUser: (row: ClientTableRow) => void;
  sort: string;
  sortOrder: 'ASC' | 'DESC';
  onSort: (sortKey: string) => void;
  roleFilter: string;
  onRoleFilterChange: (filter: string) => void;
  statusFilter: string;
  onStatusFilterChange: (filter: string) => void;
};

export const getClientsTableColumns = ({
  onActionClick,
  onEditCompany,
  onInviteUser,
  sort,
  sortOrder,
  onSort,
  roleFilter,
  onRoleFilterChange,
  statusFilter,
  onStatusFilterChange,
}: ClientsTableColumnOptions): Column<ClientTableRow>[] => [
  {
    key: 'company',
    label: 'Company',
    renderHeader: renderCustomHeader('Company'),
    render: (_value, row) => (
      <Typography sx={{ ...cellTextSx, minWidth: '160px', whiteSpace: 'nowrap' }}>
        {row.company || '-'}
      </Typography>
    ),
  },
  {
    key: 'user',
    label: 'User',
    renderHeader: renderCustomHeader('User'),
    render: (_value, row) => {
      if (row.rowType === 'company') {
        return (
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
          >
            <Typography sx={{ ...cellTextSx, minWidth: '160px', whiteSpace: 'nowrap' }}>
              {row.user || '-'}
            </Typography>
            <Button
              variant="text"
              color="primary"
              startIcon={<Add />}
              sx={{
                padding: 0,
                textTransform: 'none',
                color: '#51B8B0',
                '&:hover': { backgroundColor: 'transparent' },
              }}
              onClick={(event) => {
                event.stopPropagation();
                onInviteUser(row);
              }}
            >
              Invite User
            </Button>
          </Box>
        );
      }
      return <Typography sx={cellTextSx}>{row.user || '-'}</Typography>;
    },
  },
  {
    key: 'role',
    label: 'Role',
    renderHeader: renderFilterableHeader(
      'Role',
      CLIENT_ROLE_FILTER_OPTIONS,
      roleFilter,
      onRoleFilterChange,
    ),
    render: (_value, row) => (
      <Typography sx={{ ...cellTextSx, minWidth: '100px' }}>
        {row.role ? formatRoleLabel(row.role) : '-'}
      </Typography>
    ),
  },
  {
    key: 'dashboards',
    label: 'Dashboards',
    renderHeader: renderCustomHeader('Dashboards'),
    render: (_value, row) => (
      <DashboardCell
        row={{
          rowType: row.rowType,
          hasFullDashboardAccess: row.hasFullDashboardAccess,
          totalEntitlementCount: row.totalEntitlementCount ?? 0,
          dashboardSubmoduleNames:
            row.dashboardAccess?.submodules?.map((submodule) => submodule.submoduleName) ?? [],
        }}
      />
    ),
  },
  {
    key: 'joiningDate',
    label: 'Joining Date',
    renderHeader: renderSortableHeader('Joining Date', 'joiningDate', sort, sortOrder, onSort),
    render: (_value, row) => (
      <Typography sx={{ ...cellTextSx, minWidth: '120px' }}>
        {row.joiningDate && row.joiningDate !== '-' ? formatDate(row.joiningDate) : '-'}
      </Typography>
    ),
  },
  {
    key: 'status',
    label: 'Status',
    renderHeader: renderFilterableHeader(
      'Status',
      CLIENT_STATUS_FILTER_OPTIONS,
      statusFilter,
      onStatusFilterChange,
    ),
    render: (_value, row) => (
      <Typography sx={{ ...cellTextSx, minWidth: '100px' }}>
        {row.status ? formatStatusLabel(row.status) : '-'}
      </Typography>
    ),
  },
  {
    // Virtual column — `actions` is not on the row type; satisfies Column<T>'s `keyof T` constraint
    key: 'actions' as keyof ClientTableRow,
    label: 'Actions',
    renderHeader: renderCustomHeader('Actions'),
    render: (_value, row) => renderActionsCell(row, onEditCompany, onActionClick),
  },
];
